import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatExpansionPanelHeader } from '@angular/material/expansion';
import { ScheduleField } from '@app/models/schedule.model';
import { Staff } from '@app/models/staff.model';
import { DateService } from '@app/services/date.service';
import { DialogService } from '@app/services/dialog.service';
import { LibService } from '@app/services/lib.service';
import { NoteTitleInputParams } from '@care-connect-japan/ckweblib/lib/data/schema/params/library-params';

@Component({
  selector: 'app-schedule-field',
  templateUrl: './schedule-field.component.html',
  styleUrls: ['./schedule-field.component.scss'],
})
export class ScheduleFieldComponent implements OnInit {
  constructor(
    private dateService: DateService,
    private libService: LibService,
    private dialogService: DialogService
  ) {
  }

  @ViewChild('expander') expanderEl?: MatExpansionPanelHeader | any;
  @Input() staffList!: Staff[];
  @Input() schedule!: ScheduleField;
  @Input() duplicate?: boolean;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() collapsed: boolean = false;
  @Input() allowTitleChange: boolean = false;
  @Output() formChange = new EventEmitter<FormGroup>();
  @Output() onChangeTitle = new EventEmitter<{ ttlkbn: string, ttlcd: string }>();

  noteTitleParams!: NoteTitleInputParams;

  get containerStyle() {
    let { height, width } = (this.expanderEl?._element as ElementRef)?.nativeElement?.getBoundingClientRect() || {};
    if(!height) return {};
    return { 
      height: height + 'px',
      width: width + 'px'
    }
  }

  private _form!: FormGroup;
  @Input()
  get form(): FormGroup {
    return this._form;
  }
  set form(form: FormGroup) {
    this._form = form;

    if (this.odrstfcd && !this.odrstfcd.value && this.hasStaffList) {
      this.odrstfcd.setValue(this.staffList[0].stfcd);
    }
  }

  private _disabled = false;
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(disabled: boolean) {
    this._disabled = disabled;
    if (disabled) {
      this.form.disable();
    }
  }

  get summary() {
    return this.form.get('summary') as FormControl;
  }
  get handover() {
    return this.form.get('handover') as FormControl;
  }
  get startDate() {
    return this.form.get('startDate') as FormGroup;
  }
  get endDate() {
    return this.form.get('endDate') as FormGroup;
  }
  get status() {
    return this.form.get('status') as FormControl;
  }
  get odrstfcd() {
    return this.form.get('odrstfcd') as FormControl;
  }
  get sndstfcd() {
    return this.form.get('sndstfcd') as FormControl;
  }
  get ownunitcd() {
    return this.form.get('ownunitcd') as FormControl;
  }
  get ownstfcd() {
    return this.form.get('ownstfcd') as FormControl;
  }

  get hasStaffList() {
    return !!this.staffList && this.staffList.length > 0;
  }

  async ngOnInit(): Promise<void> {
    this.noteTitleParams = await this.libService.getNoteTitleParams(this.schedule, {
      duplicate: this.duplicate,
    });
  }

  onChange(): void {
    this.formChange.emit(this.form);
  }

  onStartDateChange(): void {
    if (!this.startDate.invalid && this.endDate.invalid) {
      this.endDate.setValue(this.startDate.value);
    }
  }

  compareDate(): void {
    const startDate = this.dateService.parseFormToDate(this.startDate);
    const endDate = this.dateService.parseFormToDate(this.endDate);

    if (!startDate || !endDate) return;

    if (startDate.getTime() >= endDate.getTime()) {
      this.endDate.setErrors({ compareDate: true });
    }
  }

  removeDuplicateVisitor(staff: Staff | null): void {
    if (staff?.stfcd === this.sndstfcd.value) {
      this.sndstfcd.setValue(null);
    }
  }

  changeTitle(pending?: { ttlkbn: string, ttlcd: string }) {
    const { ttlkbn, ttlcd } = this.schedule;
    this.dialogService.titleChange({ ttlkbn, ttlcd }, pending).afterClosed().subscribe((title: any) => {
      const rejectCondition = !title || (title.ttlkbn === ttlkbn && title.ttlcd == ttlcd)
      if (rejectCondition) return;
      const confirmDisplay = {
        cancelText: 'キャンセル',
        excuteText: '保存',
        content: `<p>サービス種別を変更します。よろしいですか？\n(※対象者・日時・担当者以外の記録はクリアされます。)</p>`,
        style: {
          'text-align': 'left'
        }
      };
      this.dialogService.confirm(confirmDisplay).afterClosed().subscribe((res) => {
        if (!res) {
          this.changeTitle(title);
          return;
        }
        this.onChangeTitle.emit({
          ttlkbn: title.ttlkbn,
          ttlcd: title.ttlcd,
        })
      })
    })
  }

  filterStaffList(): Staff[] {
    return this.staffList.filter(
      (item) =>
        item.stfcd !== this.odrstfcd.value || item.stfcd === this.sndstfcd.value
    );
  }
}
